import { Link } from "react-router-dom";
import { 
  Trophy, Users, BarChart3, Database, Target, Gamepad2, 
  ChevronRight, Zap, Bell, Calendar, TrendingUp, Shield,
  Layers, Settings, Download, Share2, Clock, Award,
  Link2, FileText, ExternalLink, Bookmark, Sparkles, Plus, Rocket, RefreshCw,
  Map, MapPin, NotebookPen, Swords, MessageSquare, Baby, IdCard
} from "lucide-react";
import { Button } from "@/components/ui/button";

// Platform logos
const PLATFORM_LOGOS = {
  xbox: {
    name: "Xbox",
    logo: "/platform-logos/xbox-green.png",
    color: "#107C10"
  },
  playstation: {
    name: "PlayStation",
    logo: "/platform-logos/playstation.png",
    color: "#003087"
  },
  steam: {
    name: "Steam",
    logo: "/platform-logos/steam.ico",
    color: "#66C0F4"
  }
};

const FEATURE_SECTIONS = [
  {
    id: "notes",
    badge: "The Notebook",
    title: "Notes & Quick Links",
    subtitle: "Your Personal Gaming Notebook",
    description: "Stop scattering your gaming brain across browser bookmarks, sticky notes, and Discord DMs. Keep notes, links, walkthroughs, build orders, and resources attached directly to each game — search them in one tap. Cheat guides and external sites are entirely optional; you can write your own.",
    icon: NotebookPen,
    color: "#06B6D4",
    features: [
      { icon: FileText, text: "Rich notes per game, achievement, and DLC" },
      { icon: Link2, text: "Save guides, wikis, and streams as quick links" },
      { icon: ExternalLink, text: "Launch external sites without losing context" },
      { icon: Bookmark, text: "One library for every gaming reference you keep" },
    ]
  },
  {
    id: "maps",
    badge: "Map Notes",
    title: "Custom Maps & Pin Markers",
    subtitle: "Mark Up Any World, Any Game",
    description: "Upload a map image from any game, drop pinned notes on collectibles, fast-travel points, side-quest triggers — whatever you want to remember. Filter by category. Hide what you've already collected. Make a map even for games that don't have one.",
    icon: Map,
    color: "#10B981",
    features: [
      { icon: MapPin, text: "Drop custom-categorized pins anywhere on a map" },
      { icon: Layers, text: "Filter visible pins by type or status" },
      { icon: NotebookPen, text: "Attach notes and images to each pin" },
      { icon: Plus, text: "Add maps for games with no official guide" },
    ]
  },
  {
    id: "priorities",
    badge: "Stay Focused",
    title: "Priorities & Backlog",
    subtitle: "Decide What's Next — Not Just What's New",
    description: "Pin the games and achievements that actually matter to you right now. Organize your backlog across every platform and stop hopping between launchers wondering what to play.",
    icon: Target,
    color: "#EF4444",
    features: [
      { icon: Target, text: "Pin top games and achievements to your radar" },
      { icon: Layers, text: "Cross-platform backlog in one ordered list" },
      { icon: TrendingUp, text: "Visual progress towards each priority" },
      { icon: Award, text: "Celebrate completions, not just unlocks" },
    ]
  },
  {
    id: "library",
    badge: "One Library",
    title: "Multi-Platform Library",
    subtitle: "Every Game, Every Console, One Place",
    description: "Connect Xbox, PlayStation, and Steam to import automatically — or manually add anything else (retro, handheld, board games, your kid's school chore chart, your wishlist). It's your library, your rules.",
    icon: Gamepad2,
    color: "#7C3AED",
    features: [
      { icon: Zap, text: "Automatic sync with Xbox, PlayStation, and Steam" },
      { icon: Plus, text: "Manually add anything — any era, any platform" },
      { icon: Layers, text: "Tag, group, and filter your entire library" },
      { icon: Download, text: "Full import/export — your data is portable" },
    ]
  },
  {
    id: "achievements",
    badge: "Progress, Organized",
    title: "Achievement Organization",
    subtitle: "Plan Your Path, Not Just Your Stats",
    description: "Achievements aren't a leaderboard race here — they're checklist items you can sort, filter, annotate, and revisit. Rarity, hints, and your own notes live side by side.",
    icon: Trophy,
    color: "#F59E0B",
    features: [
      { icon: Award, text: "Per-achievement notes, hints, and rarity" },
      { icon: Layers, text: "Group by status, type, DLC, or your own tags" },
      { icon: FileText, text: "Attach personal walkthrough steps" },
      { icon: Clock, text: "See exactly how far you've come on each game" },
    ]
  },
  {
    id: "multiplayer",
    badge: "Find Your People",
    title: "Multiplayer Coordination",
    subtitle: "Match, Schedule, Plan Together",
    description: "List the games you want to play with others, share gamertags only when you choose, broadcast availability on a 7-day schedule, and post on per-game billboards to coordinate sessions. Parental Mode flags kid-friendly hours and clean-language listings so families can find each other.",
    icon: Swords,
    color: "#22D3EE",
    features: [
      { icon: Users, text: "Discover other players by game and category" },
      { icon: Calendar, text: "7-day availability with kid-friendly hours" },
      { icon: MessageSquare, text: "Per-game billboards (cross-platform aware)" },
      { icon: Baby, text: "Parental Mode + community moderation built in" },
    ]
  },
  {
    id: "customization",
    badge: "Make It Yours",
    title: "Custom Games & Challenges",
    subtitle: "Beyond Tracking — Build Your Own",
    description: "Add games no database knows about. Invent your own achievements. Gamify chores, fitness, language learning — anything you want to track with the same level of detail you'd track a 100% run.",
    icon: Sparkles,
    color: "#EC4899",
    features: [
      { icon: Plus, text: "Add custom games not in any database" },
      { icon: Award, text: "Author your own achievements and DLCs" },
      { icon: Rocket, text: "Gamify real-life tasks and habits" },
      { icon: RefreshCw, text: "Custom challenges add replayability to favourites" },
    ]
  },
  {
    id: "analytics",
    badge: "Look Back",
    title: "Statistics & Insights",
    subtitle: "See Your Gaming Year At A Glance",
    description: "When you want them, the charts are there — gaming streaks, completion velocity, per-platform splits, time of day, and personal milestones. Glance, learn, get back to playing.",
    icon: BarChart3,
    color: "#0EA5E9",
    features: [
      { icon: TrendingUp, text: "Trends across days, weeks, and months" },
      { icon: Calendar, text: "Heatmaps for your most active gaming days" },
      { icon: Award, text: "Personal records and milestone timelines" },
      { icon: BarChart3, text: "Per-platform and per-genre breakdowns" },
    ]
  },
];

export default function FeaturesPage() {
  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 md:gap-3" data-testid="features-logo">
            <img 
              src="/logo.png" 
              alt="Game-o-Rang" 
              className="w-8 h-8 md:w-10 md:h-10 rounded-lg"
            />
            <span className="font-heading font-bold text-lg md:text-xl tracking-tight hidden sm:block">Game-o-Rang</span>
          </Link>
          <div className="flex items-center gap-2 md:gap-4">
            <Link to="/features" data-testid="nav-features-active" className="hidden sm:block">
              <Button variant="ghost" className="text-white">
                Features
              </Button>
            </Link>
            <Link to="/login" data-testid="nav-signin">
              <Button variant="ghost" className="text-white/70 hover:text-white text-sm md:text-base px-2 md:px-4">
                Sign In
              </Button>
            </Link>
            <Link to="/login?signup=true" data-testid="nav-signup">
              <Button className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold px-4 md:px-6 text-sm md:text-base">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <span className="text-sm font-mono text-[#7C3AED] uppercase tracking-widest mb-4 block">
            The Toolkit
          </span>
          <h1 className="font-heading font-black text-5xl md:text-6xl lg:text-7xl tracking-tighter uppercase leading-none mb-6">
            <span className="bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
              Built To Organize
            </span>
          </h1>
          <p className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto leading-relaxed">
            Note, map, plan, and coordinate every part of your gaming life — across every platform you play, with or without external sites or cheat guides.
          </p>

          {/* Platform logos */}
          <div className="flex flex-wrap justify-center items-center gap-8 mt-12">
            {Object.entries(PLATFORM_LOGOS).map(([key, platform]) => (
              <div 
                key={key} 
                className="flex items-center gap-3 px-4 py-2 rounded-full bg-white/5 border border-white/10"
                title={platform.name}
              >
                <img 
                  src={platform.logo} 
                  alt={platform.name} 
                  className="w-6 h-6 object-contain"
                />
                <span className="text-sm text-white/70">{platform.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Sections */}
      {FEATURE_SECTIONS.map((section, index) => (
        <section 
          key={section.id} 
          id={section.id}
          className={`py-24 md:py-32 px-6 ${index % 2 === 1 ? 'bg-white/[0.01]' : ''}`}
        >
          <div className="max-w-6xl mx-auto">
            <div className={`grid md:grid-cols-2 gap-12 md:gap-16 items-center ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
              {/* Content side */}
              <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                <span 
                  className="text-sm font-mono uppercase tracking-widest mb-4 block"
                  style={{ color: section.color }}
                >
                  {section.badge}
                </span>
                <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight mb-3">
                  {section.title}
                </h2>
                <p className="text-xl text-white/70 mb-4">{section.subtitle}</p>
                <p className="text-white/50 leading-relaxed mb-8">
                  {section.description}
                </p>

                {/* Feature list */}
                <div className="grid sm:grid-cols-2 gap-4">
                  {section.features.map((feature, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ backgroundColor: `${section.color}20` }}
                      >
                        <feature.icon className="w-4 h-4" style={{ color: section.color }} />
                      </div>
                      <span className="text-white/70 text-sm leading-relaxed">{feature.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Visual side */}
              <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                <div className="relative">
                  {/* Background glow */}
                  <div 
                    className="absolute inset-0 rounded-2xl blur-3xl opacity-20"
                    style={{ backgroundColor: section.color }}
                  />
                  
                  {/* Card */}
                  <div className="relative bg-[#0A0A0A] border border-white/10 rounded-2xl p-8 overflow-hidden">
                    {/* Icon */}
                    <div 
                      className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6"
                      style={{ backgroundColor: `${section.color}15` }}
                    >
                      <section.icon className="w-10 h-10" style={{ color: section.color }} />
                    </div>
                    
                    {/* Decorative elements */}
                    <div className="space-y-3">
                      {[...Array(4)].map((_, i) => (
                        <div 
                          key={i} 
                          className="h-3 rounded-full bg-white/5"
                          style={{ 
                            width: `${100 - (i * 15)}%`,
                            opacity: 1 - (i * 0.2)
                          }}
                        />
                      ))}
                    </div>

                    {/* Gradient overlay */}
                    <div 
                      className="absolute inset-0 opacity-10"
                      style={{
                        background: `linear-gradient(135deg, ${section.color}30, transparent 60%)`
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Additional Features Grid */}
      <section className="py-24 md:py-32 px-6 bg-gradient-to-b from-transparent via-[#7C3AED]/5 to-transparent">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm font-mono text-[#7C3AED] uppercase tracking-widest mb-4 block">
              And More
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight mb-4">
              Additional Features
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
            {[
              { icon: IdCard, title: "Gamertags & Session Sites", desc: "Per-platform tags + TA, TT, TSA, Discord — share only when you want" },
              { icon: Bell, title: "Notifications", desc: "Subtle reminders for syncs, schedule matches, and billboard activity" },
              { icon: Shield, title: "Parental Mode + Moderation", desc: "Kid-friendly hours, mark system, admin review — families welcome" },
              { icon: Download, title: "Import / Export", desc: "Backup or migrate your library — your data, your control" },
              { icon: Share2, title: "Public Signature Cards", desc: "Live, embeddable stat images for your forum & profile pages" },
              { icon: Settings, title: "Deep Customization", desc: "Tags, themes, hidden games, and per-page preferences" },
            ].map((item, i) => (
              <div 
                key={i}
                className="p-6 rounded-xl bg-white/[0.02] border border-white/10 hover:border-[#7C3AED]/30 transition-colors"
              >
                <item.icon className="w-8 h-8 text-[#7C3AED] mb-4" />
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-white/50 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mb-6">
            Ready To Get Organized?
          </h2>
          <p className="text-white/60 text-lg mb-10 max-w-xl mx-auto">
            Bring every game, every note, every collectible pin, and every gaming friend into one home. Free to start.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/login?signup=true" data-testid="features-final-cta">
              <Button 
                size="lg" 
                className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-heading font-bold uppercase tracking-wider px-10 py-6 text-lg shadow-[0_0_30px_rgba(124,58,237,0.3)] hover:shadow-[0_0_50px_rgba(124,58,237,0.5)] transition-all hover:scale-105"
              >
                Get Started Free
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/" data-testid="features-back-home">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white/20 hover:border-white/40 text-white px-8 py-6"
              >
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img 
              src="/logo.png" 
              alt="Game-o-Rang" 
              className="w-8 h-8 rounded-lg"
            />
            <span className="text-white/50 text-sm">
              © 2025 Game-o-Rang. All rights reserved.
            </span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/features" className="text-white/50 hover:text-white text-sm transition-colors">
              Features
            </Link>
            <Link to="/login" className="text-white/50 hover:text-white text-sm transition-colors">
              Sign In
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
